'use client';

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface SidebarContextType {
  isHidden: boolean;
  isCollapsed: boolean;
  /** True while the docked callcenter widget is taking over the right rail. */
  widgetOpen: boolean;
  /** True while a focus-mode route (e.g. ticket detail) forces a collapsed rail. */
  routeForceCollapsed: boolean;
  /** Effective collapsed state consumed by the sidebar: user pref OR widget OR route force. */
  effectiveCollapsed: boolean;
  hideSidebar: () => void;
  showSidebar: () => void;
  toggleCollapse: () => void;
  setWidgetOpen: (open: boolean) => void;
  setRouteForceCollapsed: (collapsed: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [isHidden, setIsHidden] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [routeForceCollapsed, setRouteForceCollapsed] = useState(false);

  const hideSidebar = useCallback(() => {
    setIsHidden(true);
  }, []);
  const showSidebar = useCallback(() => {
    setIsHidden(false);
  }, []);
  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const effectiveCollapsed = isCollapsed || widgetOpen || routeForceCollapsed;

  return (
    <SidebarContext.Provider
      value={{
        isHidden,
        isCollapsed,
        widgetOpen,
        routeForceCollapsed,
        effectiveCollapsed,
        hideSidebar,
        showSidebar,
        toggleCollapse,
        setWidgetOpen,
        setRouteForceCollapsed,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}
